var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { krBaseCSS } from '../style/base.js';
import './grid.js';
import '../button/button.js';
/**
 * A reference to an open grid dialog instance.
 *
 * Used to react to footer button clicks, close the dialog, and retrieve its result.
 *
 * @typeParam R - The type of the result returned when the dialog closes
 */
export class KRGridDialogRef {
    constructor() {
        this._resolvePromise = null;
        this._dialogElement = null;
        this._handlers = [];
        this._promise = new Promise((resolve) => {
            this._resolvePromise = resolve;
        });
    }
    /** @internal */
    setDialogElement(el) {
        this._dialogElement = el;
    }
    /**
     * Registers a handler fired whenever a footer button is clicked. The handler
     * decides what to do next — typically call `close()` with a result.
     *
     * @param handler - Called with the clicked action and the live grid.
     * @returns This ref, so calls chain into `afterClosed()`.
     */
    onAction(handler) {
        this._handlers.push(handler);
        return this;
    }
    /**
     * Closes the dialog and resolves with the given result.
     *
     * @param result - The result to return to the caller
     */
    close(result) {
        if (this._resolvePromise) {
            this._resolvePromise(result);
            this._resolvePromise = null;
        }
        this._dialogElement?.remove();
    }
    /**
     * Returns a promise that resolves when the dialog is closed.
     *
     * @returns A promise that resolves with the dialog result, or undefined if dismissed
     */
    afterClosed() {
        return this._promise;
    }
    /** @internal Invoked by the dialog element when a footer button is clicked. */
    emitAction(action, grid) {
        this._handlers.forEach((handler) => handler({
            action,
            grid,
        }));
    }
    /**
     * Show a loading spinner on a footer button — the first one unless `options.action` names another.
     * Call this from an `onAction` handler when the action kicks off async work, then
     * showActionSuccess/showActionError, or close() once the work lands. Mirrors the same methods on
     * `kr-grid` for its header action button.
     */
    showActionLoading(options = {}) {
        this._dialogElement?.showActionLoading(options);
    }
    /** Show a success state on a footer button (default text: "Done"). */
    showActionSuccess(options = {}) {
        this._dialogElement?.showActionSuccess(options);
    }
    /** Show an error state on a footer button (default text: "Failed"). */
    showActionError(options = {}) {
        this._dialogElement?.showActionError(options);
    }
    /** Put a footer button back to idle with no success/error flash. */
    resetAction(options = {}) {
        this._dialogElement?.resetAction(options);
    }
}
/**
 * Modal that hosts a `kr-grid` (dialog variant) with a footer of action buttons.
 *
 * Declarative: place `<kr-grid-dialog>` in HTML and call `.open()` / `.close()`.
 * Programmatic: use `KRGridDialog.open(config)` to create and show a dialog.
 *
 * @element kr-grid-dialog
 *
 * @fires close - Fired when a declarative dialog is closed
 * @fires action - Fired when a footer button is clicked on a declarative dialog. `detail.action` is the button id and `detail.grid` the live grid.
 * @fires selection-change - Re-targeted from the inner grid: fired when its row selection changes, with `detail.rows` the selected rows on the current page.
 */
export class KRGridDialog extends LitElement {
    constructor() {
        super(...arguments);
        this._dialogRef = null;
        this.opened = false;
        this.title = '';
        this.description = '';
        this.def = { columns: [] };
        this.actions = [];
        this._handleDocumentKeyDown = (e) => {
            if (e.key === 'Escape') {
                this.close();
            }
        };
        this._handleClose = () => {
            this.close();
        };
    }
    /**
     * Opens a grid dialog programmatically.
     */
    static open(config) {
        // Only remove other programmatic grid dialogs (those with a _dialogRef)
        document.querySelectorAll('kr-grid-dialog').forEach((el) => {
            if (el._dialogRef) {
                el.remove();
            }
        });
        const dialogRef = new KRGridDialogRef();
        const dialog = document.createElement('kr-grid-dialog');
        dialogRef.setDialogElement(dialog);
        dialog._dialogRef = dialogRef;
        dialog.title = config.title ?? '';
        dialog.description = config.description ?? '';
        dialog.def = config.def;
        dialog.actions = config.actions ?? [];
        dialog.opened = true;
        document.body.appendChild(dialog);
        return dialogRef;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._handleDocumentKeyDown);
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('opened')) {
            if (this.opened) {
                document.addEventListener('keydown', this._handleDocumentKeyDown);
            }
            else {
                document.removeEventListener('keydown', this._handleDocumentKeyDown);
            }
        }
    }
    /**
     * Opens the dialog (declarative mode).
     */
    open() {
        this.opened = true;
    }
    /**
     * Closes the dialog.
     * In programmatic mode (has _dialogRef), delegates to the dialog ref.
     * In declarative mode, sets opened=false and dispatches close event.
     */
    close() {
        if (this._dialogRef) {
            this._dialogRef.close(undefined);
            return;
        }
        this.opened = false;
        this.dispatchEvent(new CustomEvent('close', {
            bubbles: true,
            composed: true
        }));
    }
    /**
     * Show a loading spinner on a footer button — the first one unless `options.action` names another.
     * Call this from an `action` handler when the action kicks off async work, then
     * showActionSuccess/showActionError, or close() once the work lands. Mirrors `kr-grid`.
     */
    showActionLoading(options = {}) {
        this.shadowRoot?.querySelector(`.dialog__footer kr-button[data-action="${options.action ?? this.actions[0]?.id}"]`)?.showLoading();
    }
    /** Show a success state on a footer button (default text: "Done"). */
    showActionSuccess(options = {}) {
        this.shadowRoot?.querySelector(`.dialog__footer kr-button[data-action="${options.action ?? this.actions[0]?.id}"]`)?.showSuccess(options.text ?? 'Done');
    }
    /** Show an error state on a footer button (default text: "Failed"). */
    showActionError(options = {}) {
        this.shadowRoot?.querySelector(`.dialog__footer kr-button[data-action="${options.action ?? this.actions[0]?.id}"]`)?.showError(options.text ?? 'Failed');
    }
    /** Put a footer button back to idle with no success/error flash. */
    resetAction(options = {}) {
        this.shadowRoot?.querySelector(`.dialog__footer kr-button[data-action="${options.action ?? this.actions[0]?.id}"]`)?.reset();
    }
    _handleAction(action) {
        const grid = this.shadowRoot?.querySelector('kr-grid');
        if (!grid) {
            return;
        }
        if (this._dialogRef) {
            this._dialogRef.emitAction(action, grid);
            return;
        }
        this.dispatchEvent(new CustomEvent('action', {
            detail: {
                action,
                grid,
            },
            bubbles: true,
            composed: true
        }));
    }
    _handleBackdropClick(e) {
        if (e.target.classList.contains('backdrop')) {
            this.close();
        }
    }
    render() {
        return html `
      <div class="backdrop" @click=${this._handleBackdropClick}></div>
      <div class="dialog">
        <button
          class="dialog__close"
          aria-label="Close"
          @click=${this._handleClose}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
        ${this.title || this.description ? html `
          <div class="dialog__header">
            ${this.title ? html `<h2 class="dialog__title">${this.title}</h2>` : nothing}
            ${this.description ? html `<p class="dialog__description">${this.description}</p>` : nothing}
          </div>
        ` : nothing}
        <div class="dialog__body">
          <kr-grid variant="dialog" .def=${this.def}></kr-grid>
          ${this.actions.length ? html `
            <div class="dialog__footer">
              ${this.actions.map(action => html `
                <kr-button
                  data-action=${action.id}
                  color=${action.color ?? 'primary'}
                  variant=${action.variant ?? 'outline'}
                  @click=${() => this._handleAction(action.id)}
                >
                  ${action.label}
                </kr-button>
              `)}
            </div>
          ` : nothing}
        </div>
      </div>
    `;
    }
}
KRGridDialog.styles = [
    krBaseCSS,
    css `
      :host {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: none;
        align-items: center;
        justify-content: center;
      }

      :host([opened]) {
        display: flex;
      }

      .backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
      }

      .dialog {
        position: relative;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 12px;
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
        max-height: 90vh;
        height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        width: 90vw;
        max-width: 1200px;
      }

      .dialog__close {
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        padding: 0;
        background: none;
        border: none;
        border-radius: 6px;
        color: #1a1a2e;
        cursor: pointer;
        transition: background-color 0.15s, color 0.15s;
      }

      .dialog__close:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #000;
      }

      .dialog__close svg {
        width: 22px;
        height: 22px;
      }

      .dialog__header {
        flex-shrink: 0;
        /* Extra right padding so the title clears the absolutely-positioned X. */
        padding: 24px 60px 0 24px;
      }

      .dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: #1f2937;
        margin: 0;
      }

      .dialog__description {
        font-size: 14px;
        line-height: 1.5;
        color: black;
        margin: 8px 0 0;
        /* Cap the measure so long descriptions wrap as a readable paragraph
           instead of stretching the full dialog width. */
        max-width: 70ch;
      }

      /* The grid fills the space above the footer and self-scrolls. */
      .dialog__body {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }

      kr-grid {
        flex: 1;
        min-height: 0;
      }

      .dialog__footer {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        align-items: center;
        padding: 16px 24px;
        flex-shrink: 0;
        border-top: 1px solid rgba(0, 0, 0, 0.1);
      }
    `,
];
__decorate([
    property({
        type: Boolean,
        reflect: true
    })
], KRGridDialog.prototype, "opened", void 0);
__decorate([
    property({ type: String })
], KRGridDialog.prototype, "title", void 0);
__decorate([
    property({ type: String })
], KRGridDialog.prototype, "description", void 0);
__decorate([
    property({ attribute: false })
], KRGridDialog.prototype, "def", void 0);
__decorate([
    property({ attribute: false })
], KRGridDialog.prototype, "actions", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-grid-dialog')) {
    customElements.define('kr-grid-dialog', KRGridDialog);
}
//# sourceMappingURL=grid-dialog.js.map